/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import toNumber from 'lodash/toNumber';
import { Section, Item } from 'react-stately';

import { Autosuggest, Icon } from 'sarsaparilla';

import { LocationSuggestion } from 'shared-ui/search-autosuggest';

import { toTitleCase } from '../utils/stringUtils';
import { addUniqueIdsToSuggestions, getSuggestionByKey } from '../utils/tripUtils';
import * as tripPlannerActions from '../actions/tripPlanner';

const propTypes = {
    placeholder: PropTypes.string,
    destination: PropTypes.object,
    destinationIndex: PropTypes.number,
    tripPlannerSearchSuggestions: PropTypes.any,
    dispatch: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    searchInputIcon: PropTypes.object,
    validate: PropTypes.bool,
    showAddresses: PropTypes.bool,
};

const defaultProps = {
    placeholder: 'Enter name, location, or address',
    validate: false,
    destination: {},
    destinationIndex: -1,
    onBlur: () => {},
    showAddresses: true,
};

// SearchInput is search input box and autosuggestions combo
class TripDestinationSearchInput extends React.Component {
    static propTypes = propTypes;

    static defaultProps = defaultProps;

    constructor(props) {
        super(props);

        this.state = {
            searchText:
                props.destination && props.destination.name ? props.destination.name : '',
            selectedKey: null,
        };

        this.onSuggestionsFetchRequested = debounce(
            this.onSuggestionsFetchRequested.bind(this),
            200,
            { leading: true, trailing: true }
        );
        this.getSuggestionValue = this.getSuggestionValue.bind(this);
        this.onSuggestionSelected = this.onSuggestionSelected.bind(this);

        this.handleOnInputChange = this.handleOnInputChange.bind(this);
        this.handleOnSelect = this.handleOnSelect.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.handleSelectionChange = this.handleSelectionChange.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
    }

    handleFocus() {
        this.onSuggestionsFetchRequested(this.state.searchText);
    }

    handleOnSelect(key) {
        const suggestion = getSuggestionByKey(key, this.getSuggestions());

        if (!suggestion) return;

        this.onSuggestionSelected(suggestion, key);
    }

    handleSelectionChange(key) {
        if (key) {
            this.handleOnSelect(key);
        }
    }

    handleOnInputChange(value) {
        this.setState({ searchText: value, selectedKey: null });

        this.onSuggestionsFetchRequested(value);
    }

    handleClear() {
        this.setState({
            searchText: '',
            selectedKey: null,
        });

        if (this.props.onChange) {
            const destination = {
                entity_id: null,
                entity_type: null,
                items: [],
                lat: null,
                long: null,
                name: '',
                id: this.props.destination.id,
            };

            this.props.onChange(destination, this.props.destinationIndex);
        }
    }

    // when auto suggests needs more data
    onSuggestionsFetchRequested(value) {
        if (value) {
            this.props.dispatch(tripPlannerActions.fetchDestinationSuggestions(value));
        }
    }

    // when auto suggestion was selected somehow using nav
    onSuggestionSelected(suggestion, selectedKey) {
        const suggestionText = this.getSuggestionValue(suggestion);

        const destination = { ...this.props.destination };

        this.setState(
            {
                searchText: suggestionText,
                selectedKey,
            },
            () => {
                this.onSuggestionsFetchRequested(suggestionText);

                destination.name = suggestionText;
                destination.lat = toNumber(suggestion.lat);
                destination.lng = toNumber(suggestion.lng);
                destination.entity_id = suggestion.entity_id;
                destination.entity_type = suggestion.entity_type;
                destination.description = suggestion.description;
                destination.preview_image_url = suggestion.preview_image_url;
                destination.items = [];
                if (
                    suggestion.entity_id &&
                    (suggestion.entity_type === 'campground' ||
                        suggestion.entity_type === 'tour' ||
                        suggestion.entity_type === 'permit' ||
                        suggestion.entity_type === 'vehiclepermit' ||
                        suggestion.entity_type === 'activitypass' ||
                        suggestion.entity_type === 'treepermit' ||
                        suggestion.entity_type === 'ticketfacility')
                ) {
                    destination.items = [
                        {
                            entity_id: suggestion.entity_id,
                            entity_type: suggestion.entity_type,
                            lat: toNumber(suggestion.lat),
                            lng: toNumber(suggestion.lng),
                            name: suggestion.name,
                            reservable: suggestion.reservable,
                        },
                    ];
                }
                if (this.props.onChange) {
                    this.props.onChange(destination, this.props.destinationIndex);
                }
            }
        );
    }

    // construct suggestion sections from fetched data
    getSuggestions() {
        const sections = [];

        if (!this.state.searchText) return sections;

        if (
            this.props.showAddresses &&
            this.props.tripPlannerSearchSuggestions.suggestions &&
            this.props.tripPlannerSearchSuggestions.suggestions.length > 0
        ) {
            sections.push(
                ...this.props.tripPlannerSearchSuggestions.suggestions.flatMap(
                    (suggestion) => suggestion
                )
            );
        }

        if (
            this.props.tripPlannerSearchSuggestions.inventory_suggestions &&
            this.props.tripPlannerSearchSuggestions.inventory_suggestions.length > 0
        ) {
            let suggestions = [];
            this.props.tripPlannerSearchSuggestions.inventory_suggestions.forEach(
                (suggestion) => {
                    // filter out unneeded suggestions
                    if (
                        suggestion.lat !== 0 &&
                        suggestion.entity_type !== 'kb' &&
                        suggestion.entity_type !== 'cms' &&
                        suggestion.entity_type !== 'pass'
                    ) {
                        suggestions.push(suggestion);
                    }
                }
            );
            if (suggestions.length > 3) {
                suggestions = suggestions.slice(0, 3);
            }
            sections[sections.length] = {
                title: 'Recreation Areas, Facilities, Tours, Trails',
                suggestions,
            };
        }

        return sections;
    }

    // get search suggestion string to display in the drop down
    getSuggestionValue(suggestion) {
        let suggestionText = suggestion.text;
        if (suggestion.is_inventory) {
            // if suggestion is associated with an inventory name, then capitalize the name to clean it up
            suggestionText = toTitleCase(suggestionText);
        }

        return suggestionText;
    }

    isValid() {
        if (!this.props.showErrors) return true;
        return this.props.destination.lat && this.props.destination.lng;
    }

    // render search input component
    render() {
        const suggestions = this.getSuggestions();

        let searchInputIcon;
        if (this.props.searchInputIcon) {
            searchInputIcon = this.props.searchInputIcon;
        } else {
            searchInputIcon = <Icon iconName="pin" />;
        }

        return (
            <div className="trip-planner-destination-search-input">
                <Autosuggest
                    allowsEmptyCollection
                    allowsCustomValue
                    isLabelVisible={false}
                    label="Search for a destination"
                    menuTrigger="focus"
                    id={`destination_search_input_${this.props.destinationIndex}`}
                    iconBeforeElement={searchInputIcon}
                    selectedKey={this.state.selectedKey}
                    isInvalid={this.props.validate && !this.isValid()}
                    placeholder={this.props.placeholder}
                    inputValue={this.state.searchText}
                    onInputChange={this.handleOnInputChange}
                    onSelect={this.handleOnSelect}
                    onClear={this.handleClear}
                    items={suggestions}
                    onSelectionChange={this.handleSelectionChange}
                    onFocus={this.handleFocus}
                    onBlur={this.props.onBlur}
                >
                    {(option) => {
                        return option.title ? (
                            <Section
                                key={option.title}
                                title={option.title}
                                items={option.suggestions}
                            >
                                {(item) => (
                                    <Item
                                        key={item.id}
                                        textValue={this.getSuggestionValue(item)}
                                    >
                                        <LocationSuggestion
                                            suggestion={item}
                                            searchText={this.state.searchText}
                                        />
                                    </Item>
                                )}
                            </Section>
                        ) : (
                            <Item
                                key={option.id}
                                textValue={this.getSuggestionValue(option)}
                            >
                                <LocationSuggestion
                                    suggestion={option}
                                    searchText={this.state.searchText}
                                />
                            </Item>
                        );
                    }}
                </Autosuggest>
            </div>
        );
    }
}

function select(state) {
    return {
        tripPlannerSearchSuggestions: {
            ...state.tripPlannerSearchSuggestions,
            suggestions: addUniqueIdsToSuggestions(
                state.tripPlannerSearchSuggestions.suggestions
            ),
            inventory_suggestions: addUniqueIdsToSuggestions(
                state.tripPlannerSearchSuggestions.inventory_suggestions
            ),
        },
    };
}

export default connect(select)(TripDestinationSearchInput);

// cSpell:ignore activitypass, ticketfacility, treepermit, vehiclepermit
